import { useQuery } from "@tanstack/react-query";
import { useAuthContext } from "@mavenmm/teamwork-auth";
import { useAdminOverride } from "./useAdminOverride";

export interface UserTeam {
  id: string;
  teamName: string;
}

export interface UserPod {
  id: string;
  name: string;
}

export interface UserSubPod {
  id: string;
  name: string;
  pod_id: string;
}

export interface CurrentUser {
  id: string;
  first_name: string;
  last_name: string;
  email_address: string;
  team: UserTeam | null;
  teamId: string | null;
  pods: UserPod[];
  sub_pods: UserSubPod[];
}

const ME_QUERY = `
  query GetCurrentUser {
    me {
      id
      first_name
      last_name
      email_address
      teamId
      team {
        id
        teamName
      }
      pods {
        id
        name
      }
      sub_pods {
        id
        name
        pod_id
      }
    }
  }
`;

export function useCurrentUser() {
  const { getAccessToken, isAuthenticated } = useAuthContext();
  const { overrideEmail } = useAdminOverride();

  const query = useQuery({
    queryKey: ["currentUser", overrideEmail],
    queryFn: async () => {
      const token = getAccessToken();
      if (!token) throw new Error("No access token");

      const headers: Record<string, string> = {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      };
      if (overrideEmail) {
        headers["x-admin-user-override"] = overrideEmail;
      }

      const response = await fetch("/.netlify/functions/graphql-proxy", {
        method: "POST",
        credentials: "include",
        headers,
        body: JSON.stringify({ query: ME_QUERY }),
      });

      if (!response.ok) {
        throw new Error(`GraphQL request failed: ${response.status}`);
      }

      const json = await response.json();
      if (json.errors?.length) {
        throw new Error(json.errors[0].message);
      }

      return json.data?.me as CurrentUser | null;
    },
    enabled: isAuthenticated,
    staleTime: 30 * 60 * 1000, // 30 minutes - user data doesn't change often
  });

  return {
    user: query.data ?? null,
    isLoading: query.isLoading,
    errors: query.error ? [query.error.message] : null,
    refetch: query.refetch,
  };
}
